import { useState, useCallback } from 'react';
import { adminAPI } from '../../services/api';

/**
 * useAdminOps — Admin-wide ops: seed database, clear database, CSV export.
 * Takes a `refreshData` callback to refresh the dashboard after seed/delete.
 */
export const useAdminOps = (refreshData) => {
  const [showSeedConfirm, setShowSeedConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState(null);

  const handleSeedDatabase = useCallback(async () => {
    setSeedLoading(true);
    setSettingsMessage(null);
    try {
      const result = await adminAPI.seedDatabase();
      setSettingsMessage({
        type: 'success',
        text: `Database succesvol gevuld! ${result.seeded?.assessments || 0} quickscans, ${result.seeded?.usecases || 0} use cases toegevoegd.`,
      });
      await refreshData?.();
    } catch (error) {
      console.error('Failed to seed database:', error);
      setSettingsMessage({
        type: 'error',
        text: 'Fout bij het vullen van de database. Probeer het opnieuw.',
      });
    } finally {
      setSeedLoading(false);
      setShowSeedConfirm(false);
    }
  }, [refreshData]);

  const handleDeleteAllData = useCallback(async () => {
    setDeleteLoading(true);
    setSettingsMessage(null);
    try {
      const result = await adminAPI.clearDatabase();
      setSettingsMessage({
        type: 'success',
        text: `Alle data verwijderd! ${result.deleted?.assessments || 0} quickscans, ${result.deleted?.usecases || 0} use cases verwijderd.`,
      });
      await refreshData?.();
    } catch (error) {
      console.error('Failed to delete data:', error);
      setSettingsMessage({
        type: 'error',
        text: 'Fout bij het verwijderen van data. Probeer het opnieuw.',
      });
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  }, [refreshData]);

  const exportCSV = useCallback((data, filename) => {
    if (!data || data.length === 0) return;
    // RFC-4180 compliant escaping: wrap in double-quotes if value contains comma/quote/newline,
    // and double any internal quotes.
    const escape = (val) => {
      if (val === null || val === undefined) return '';
      const s = typeof val === 'object' ? JSON.stringify(val) : String(val);
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const headerKeys = Object.keys(data[0] || {});
    const headers = headerKeys.map(escape).join(',');
    const rows = data.map((row) => headerKeys.map((k) => escape(row[k])).join(','));
    const csv = [headers, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return {
    showSeedConfirm, setShowSeedConfirm,
    showDeleteConfirm, setShowDeleteConfirm,
    seedLoading, deleteLoading,
    settingsMessage,
    handleSeedDatabase,
    handleDeleteAllData,
    exportCSV,
  };
};

export default useAdminOps;
